<template>
  <div class="container">
    <div class="col-auto my-5">
      <h1>Editace ceníku:</h1>
    </div>
    <div class="row my-3 justify-content-between">
      <div class="col-auto d-flex align-items-center">
        <input type="text" class="form-control me-3" id="search" name="search" v-model="search"
               placeholder="hledaný text...">
        <i class="bi bi-x-circle fs-5 hover-icon" @click="resetSearch()"></i>
      </div>
      <div class="col-auto">
        <button class="btn btn-outline-success btn-lg" @click="newItemToggle">
          <i class="bi-plus-circle-dotted"></i>
          <strong>&nbsp;&nbsp;Nová položka</strong>
        </button>
      </div>
    </div>
    <table class="table table-light table-striped table-hover">
      <thead class="table-head">
      <tr>
        <th class="text-start sortable" @click="toggleSort('name')">
          Název <i class="bi" :class="sortIconClass('name')"></i>
        </th>
        <th class="text-center sortable" @click="toggleSort('thickness')">
          <span :class="{ 'filter-active': filterThicknesses.length > 0 }">Síla</span>
          <i class="bi" :class="sortIconClass('thickness')"></i>
        </th>
        <th class="text-center">
          <span :class="{ 'filter-active': filterSurfaces.length > 0 }">Povrch</span>
        </th>
        <th class="text-center sortable" @click="toggleSort('material')">
          <span :class="{ 'filter-active': filterMaterials.length > 0 }">Materiál</span>
          <i class="bi" :class="sortIconClass('material')"></i>
        </th>
        <th class="text-center sortable" @click="toggleSort('price_list')">
          <span :class="{ 'filter-active': filterPriceLists.length > 0 }">Ceník</span>
          <i class="bi" :class="sortIconClass('price_list')"></i>
        </th>
        <th class="text-end sortable" @click="toggleSort('price')">
          Cena <i class="bi" :class="sortIconClass('price')"></i>
        </th>
        <th>&nbsp;</th>
      </tr>
      <tr class="filter-row">
        <th></th>
        <th class="text-center">
          <MultiSelectFilter id="filter-thickness" label="Síla" :options="thicknessOptions"
                              v-model="filterThicknesses"/>
        </th>
        <th class="text-center">
          <MultiSelectFilter id="filter-surface" label="Povrch" :options="surfaceFilterOptions"
                              v-model="filterSurfaces"/>
        </th>
        <th class="text-center">
          <MultiSelectFilter id="filter-material" label="Materiál" :options="materialOptions"
                              v-model="filterMaterials"/>
        </th>
        <th class="text-center">
          <MultiSelectFilter id="filter-pricelist" label="Ceník" :options="priceListOptions"
                              v-model="filterPriceLists"/>
        </th>
        <th></th>
        <th></th>
      </tr>
      </thead>
      <tbody>
      <tr v-if="!loaded">
        <td colspan="7" class="text-center text-muted py-4">Načítání…</td>
      </tr>
      <tr v-else class="align-middle" v-for="(item, key) in priceListItems" :key="key" @click="editItemToggle(key)">
        <td>{{ item.name }}</td>
        <td class="text-center">{{ store.getters['price_list/getThicknessById'](item.thickness_id)?.name || '' }}</td>
        <td class="text-center">{{ item.surface }}</td>
        <td class="text-center">{{ store.getters['price_list/getMaterialById'](item.material_id)?.name || '' }}</td>
        <td class="text-center">{{ store.getters['price_list/getPriceListById'](item.price_list_id)?.name || '' }}</td>
        <td class="text-end">{{ formatFloat(item.price, 2) }}&nbsp;Kč</td>
        <td class="text-end">
          <div class="m-2">
            <i class="bi-trash-fill myIcon" @click.stop="deleteItem(item.id)"></i>
          </div>
        </td>
      </tr>
      </tbody>
    </table>
    <nav aria-label="Page navigation example" v-if="loaded && pagination">
      <ul class="pagination justify-content-end">
        <li class="page-item"><a class="page-link" href="#" @click.prevent="setFirstPage()"> &lt;&lt; </a></li>
        <li class="page-item"><a class="page-link" href="#" @click.prevent="moveBackwardPage()"> &lt; </a></li>
        <li class="page-item" v-for="n in totalPages" :key="n" :class="{ active: n === currentPage }">
          <a class="page-link" href="#" @click.prevent="setCurrentPage(n)">{{ n }}</a>
        </li>
        <li class="page-item"><a class="page-link" href="#" @click.prevent="moveForwardPage()"> &gt; </a></li>
        <li class="page-item"><a class="page-link" href="#" @click.prevent="setLastPage()"> &gt;&gt; </a></li>
        <li style="margin-left: 10px;">
          <select class="form-select" v-model="perPage">
            <option v-for="per_page in perPageSelect" :key="per_page" :value="per_page"> {{ per_page }}</option>
          </select>
        </li>
      </ul>
    </nav>
    <modal-base-new
        v-if="editVisible"
        :is-visible="editVisible"
        title="Editace položky ceníku: "
        v-model:isVisible="editVisible"
        @save="saveEditedItem">
      <PriceListItemForm v-if="editVisible" v-model="editItem"/>
    </modal-base-new>
    <modal-base-new
        v-if="newItemVisible"
        :is-visible="newItemVisible"
        title="Nová položka ceníku: "
        v-model:isVisible="newItemVisible"
        @save="saveNewItem">
      <PriceListItemForm v-if="newItemVisible" v-model="saveItem"/>
    </modal-base-new>
  </div>
</template>

<script setup>
import {ref, onMounted, watch, computed} from 'vue'
import PriceListItemForm from "@/components/admin/PriceListItemForm.vue"
import ModalBaseNew from "@/components/base/ModalBaseNew.vue";
import MultiSelectFilter from "@/components/base/MultiSelectFilter.vue";
import api from "@/api/api"
import {formatFloat} from "@/common/functions"
import {useStore} from "vuex";

const priceListItems = ref([]);
const loaded = ref(false);
const search = ref('');
const editVisible = ref(false);
const editItem = ref(null);
const newItemVisible = ref(false);
const saveItem = ref(null);
const pagination = ref(null);
const currentPage = ref(1);
const perPage = ref(100);
const perPageSelect = ref([10, 20, 50, 100, 500]);

// filtry
const filterMaterials = ref([]);
const filterThicknesses = ref([]);
const filterSurfaces = ref([]);
const filterPriceLists = ref([]);
const surfaceOptions = ref([]);

// řazení
const sortBy = ref(null); // 'name' | 'material' | 'price_list' | 'price' | null
const sortDir = ref('asc');

// Computed property pro bezpečný přístup k total_pages
const totalPages = computed(() => {
  return pagination.value?.total_pages || 1;
});

const store = useStore()

const materialOptions = computed(() =>
    Object.values(store.getters['price_list/getAllMaterials']).map(m => ({value: m.id, label: m.name})));
const thicknessOptions = computed(() =>
    Object.values(store.getters['price_list/getAllThicknesses']).map(t => ({value: t.id, label: t.name})));
const priceListOptions = computed(() =>
    Object.values(store.getters['price_list/getAllPriceList']).map(p => ({value: p.id, label: p.name})));
const surfaceFilterOptions = computed(() =>
    surfaceOptions.value.map(s => ({value: s, label: s})));

const reloadData = async () => {
  loaded.value = false;
  try {
    const data = await api.listPriceListItems({
      page: currentPage.value,
      perPage: perPage.value,
      search: search.value,
      priceLists: filterPriceLists.value,
      materials: filterMaterials.value,
      thicknesses: filterThicknesses.value,
      surfaces: filterSurfaces.value,
      sortBy: sortBy.value,
      sortDir: sortDir.value,
    });
    priceListItems.value = data.data || [];
    pagination.value = data.pagination || { total_pages: 1 };
  } catch (error) {
    console.error("Chyba při načítání dat:", error);
    priceListItems.value = [];
    pagination.value = { total_pages: 1 };
  }
  loaded.value = true;
}

const toggleSort = (column) => {
  if (sortBy.value === column) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortBy.value = column;
    sortDir.value = 'asc';
  }
}

const sortIconClass = (column) => {
  if (sortBy.value !== column) {
    return 'bi-arrow-down-up text-muted';
  }
  return sortDir.value === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill';
}

const editItemToggle = (itemIndex) => {
  editVisible.value = true;
  editItem.value = {...priceListItems.value[itemIndex]};
}

const newItemToggle = () => {
  newItemVisible.value = true;
  saveItem.value = {
    price_list_id: 5,
    material_id: 1,
    units_id: 4,
    thickness_id: 1,
    ext_id: null,
    name: null,
    price: null,
    unit_count: null,
    weight: 60,
    surface: "lesklý",
    is_directional: false,
    order_only: false,
    cutoff: 1.10,
    rate: 1.3,
    rate_absolute: 0,
    dimension_x: null,
    dimension_y: null,
    internal_note: null
  };
}

const setCurrentPage = async (page) => {
  currentPage.value = page;
  await reloadData();
}

const setFirstPage = async () => {
  currentPage.value = 1;
  await reloadData();
}

const setLastPage = async () => {
  currentPage.value = totalPages.value;
  await reloadData();
}

const moveForwardPage = async () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value += 1;
    await reloadData();
  }
}

const moveBackwardPage = async () => {
  if (currentPage.value > 1) {
    currentPage.value -= 1;
    await reloadData();
  }
}

const saveEditedItem = async () => {
  try {
    const index = priceListItems.value.findIndex(item => item.id === editItem.value.id);
    await api.updatePriceListItem(editItem.value.id, editItem.value);
    if (index !== -1) {
      priceListItems.value[index] = editItem.value;
    }
    editVisible.value = false;
  } catch (error) {
    console.error("Chyba při ukládání položky:", error);
    // Zde můžete přidat zobrazení chybové hlášky uživateli
  }
};

const saveNewItem = async () => {
  try {
    await api.insertPriceListItem(saveItem.value);
    newItemVisible.value = false;
    await reloadData();
  } catch (error) {
    console.error("Chyba při vytváření nové položky:", error);
    // Zde můžete přidat zobrazení chybové hlášky uživateli
  }
}

const resetSearch = () => {
  search.value = '';
}

const deleteItem = async (item_id) => {
  try {
    await api.deletePriceListItem(item_id);
    await reloadData();
  } catch (error) {
    console.error("Chyba při mazání položky:", error);
    // Zde můžete přidat zobrazení chybové hlášky uživateli
  }
}

// watchers
watch(search, async () => {
  currentPage.value = 1; // Reset na první stránku při vyhledávání
  await reloadData();
})

watch(perPage, async () => {
  currentPage.value = 1; // Reset na první stránku při změně počtu záznamů na stránku
  await reloadData();
})

watch(filterMaterials, async () => {
  currentPage.value = 1;
  await reloadData();
})

watch(filterThicknesses, async () => {
  currentPage.value = 1;
  await reloadData();
})

watch(filterSurfaces, async () => {
  currentPage.value = 1;
  await reloadData();
})

watch(filterPriceLists, async () => {
  currentPage.value = 1;
  await reloadData();
})

watch([sortBy, sortDir], async () => {
  currentPage.value = 1;
  await reloadData();
})

onMounted(async () => {
  try {
    const [, surfaces] = await Promise.all([
      store.dispatch('price_list/loadAllLists'),
      api.listPriceListItemSurfaces(),
    ]);
    surfaceOptions.value = surfaces || [];
    await reloadData();
  } catch (error) {
    console.error("Chyba při načítání dat:", error);
    loaded.value = true; // Nastavíme loaded na true, abychom zobrazili alespoň prázdnou tabulku
  }
})
</script>

<style scoped>
.sortable {
  cursor: pointer;
  user-select: none;
}

.filter-row th {
  padding-top: .25rem;
  padding-bottom: .5rem;
  font-weight: normal;
}

.filter-active {
  font-weight: 700;
  color: var(--bs-primary);
}
</style>

